import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { toast } from "sonner";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  profile: null,
  loading: true,
  signOut: async () => {},
  refreshProfile: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Handle auth error in URL hash (e.g. expired/reused confirmation link)
    const hash = window.location.hash;
    if (hash && hash.includes("error=")) {
      const params = new URLSearchParams(hash.replace("#", ""));
      const errorCode = params.get("error_code");
      const errorDesc = params.get("error_description");

      // Clean the hash from URL
      window.history.replaceState(null, "", window.location.pathname);

      const messages: Record<string, string> = {
        otp_expired: "Vahvistuslinkki on jo käytetty tai vanhentunut. Voit kirjautua sisään normaalisti.",
        access_denied: "Linkki on jo käytetty tai vanhentunut. Voit kirjautua sisään normaalisti.",
      };

      const msg = messages[errorCode || ""] || errorDesc?.replace(/\+/g, " ") || "Linkki on virheellinen tai vanhentunut.";
      toast.info("Linkki on jo käytetty", { description: msg, duration: 8000 });
      // Don't return — still set up auth listener so existing session works
    }

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const isConfirmed = Boolean(session?.user?.email_confirmed_at);
        if (session?.user && !isConfirmed) {
          void supabase.auth.signOut();
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        if (!session?.user) {
          setProfile(null);
        }
        setLoading(false);
      }
    );

    // Then get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      const isConfirmed = Boolean(session?.user?.email_confirmed_at);
      if (session?.user && !isConfirmed) {
        void supabase.auth.signOut();
        setSession(null);
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = useCallback(async (uid: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", uid)
      .single();
    setProfile(data);
  }, []);

  // Fetch profile separately when user changes — avoids blocking auth state
  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }
    fetchProfile(user.id);
    // Sync subscription status from Stripe on login
    syncSubscription();
  }, [user, fetchProfile]);

  // Keep profile fresh when user returns to the tab.
  useEffect(() => {
    if (!user) return;

    const refresh = () => {
      void fetchProfile(user.id);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user, fetchProfile]);

  // Realtime sync for profile billing/status changes done from admin tools.
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`profile-changes-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "profiles",
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          const next = payload.new as Profile;
          setProfile(next);
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user]);

  const syncSubscription = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) return;
      await supabase.functions.invoke("check-subscription", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      // Re-fetch profile to get Stripe-synced data
      if (user) await fetchProfile(user.id);
    } catch (e) {
      console.error("Subscription sync failed:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, fetchProfile]);

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider value={{ session, user, profile, loading, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}
